import ApartmentIcon from '@mui/icons-material/Apartment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { currentInvestment } from '../../../models/Investor';
import ExtendedLiquid from '../../../services/ExtendedLiquid';
import { Palette } from 'Palette';
import { StyledCard, StyledTypography } from '../../styles/styles';

export interface PipelineCardInvestor {
  id: string;
  status: string;
  eoiSubmitted?: boolean;
  indicativeInvestment?: number | null;
  preferredInvestment?: number | null;
  committedInvestment?: number | null;
}

export interface FundOpportunityPipelineCardProps {
  fundOpportunityId: string;
  fundName: string;
  investor?: PipelineCardInvestor;
  fundSize?: number | null;
  fundStatus?: string | null;
  tone?: keyof typeof Palette;
  investorNavigateUrl?: string;
  onPreviewNavigate?: () => void;
  onPromote?: () => void;
  onDemote?: () => void;
  onDelete?: () => void;
  /** Kanban column height; pass false for content-sized cards (e.g. chat popover). */
  fixedHeight?: string | false;
  /** Chat popover: kanban hover overlay with View Investor only. */
  previewMode?: boolean;
}

export function pipelineToneForInvestorStatus(
  status: string,
  eoiSubmitted?: boolean,
): keyof typeof Palette {
  if (status === 'LEAD' && eoiSubmitted) {
    return 'tone4';
  }
  switch (status) {
    case 'CANDIDATE':
      return 'tone2';
    case 'LEAD':
      return 'tone3';
    case 'OPPORTUNITY':
      return 'tone1';
    case 'COMMITTED_INVESTOR':
      return 'tone2';
    default:
      return 'Primary';
  }
}

const FundOpportunityPipelineCard: React.FC<FundOpportunityPipelineCardProps> = ({
  fundOpportunityId,
  fundName,
  investor,
  fundSize,
  fundStatus,
  tone = 'Primary',
  investorNavigateUrl,
  onPreviewNavigate,
  onPromote,
  onDemote,
  onDelete,
  fixedHeight = '120px',
  previewMode = false,
}) => {
  const navigate = useNavigate();
  const showInvestorPreviewOverlay = previewMode && Boolean(investor);
  const showKanbanOverlay = !previewMode && Boolean(investor || onPromote || onDemote || onDelete);
  const showPipelineOverlay = showInvestorPreviewOverlay || showKanbanOverlay;
  const useCompactHeight = previewMode || fixedHeight === false;

  const handleCardClick = () => {
    if (previewMode) {
      return;
    }
    navigate(`/opportunities/${fundOpportunityId}`);
  };

  const handleViewInvestor = (event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(
      investorNavigateUrl ?? `/opportunities/${fundOpportunityId}/investors/${investor!.id}`,
    );
    if (previewMode) {
      onPreviewNavigate?.();
    }
  };

  const investmentAmount = investor
    ? currentInvestment(investor as Parameters<typeof currentInvestment>[0])
    : null;

  return (
    <StyledCard
      tone={tone}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        ...(useCompactHeight
          ? { height: 'auto', flex: '0 0 auto' }
          : { height: fixedHeight, flexShrink: 0 }),
        width: previewMode ? '200px' : '180px',
        margin: previewMode ? 0 : '0 8px 12px 0',
        overflow: 'hidden',
        ...(showPipelineOverlay && {
          '&:hover .action-overlay': {
            opacity: 1,
            visibility: 'visible',
          },
        }),
      }}
      onClick={handleCardClick}
    >
      <Stack spacing={0.5} sx={{ p: 1, ...(useCompactHeight ? {} : { height: '100%' }) }}>
        <StyledTypography
          tone={tone}
          sx={{
            fontSize: '0.65rem',
            fontWeight: 700,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.1,
          }}
        >
          {fundName || 'Unnamed Opportunity'}
        </StyledTypography>

        <StyledTypography
          tone={tone}
          sx={{
            fontSize: '0.55rem',
            opacity: 0.8,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {investor ? investor.status : fundStatus}
        </StyledTypography>

        {investor && investor.status !== 'CANDIDATE' && investmentAmount ? (
          <StyledTypography
            tone={tone}
            sx={{
              fontSize: '0.45rem',
              opacity: 0.7,
              fontStyle: 'italic',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Investment: {ExtendedLiquid.currencyMillions(investmentAmount, true)}
          </StyledTypography>
        ) : fundSize ? (
          <StyledTypography
            tone={tone}
            sx={{
              fontSize: '0.45rem',
              opacity: 0.7,
              fontStyle: 'italic',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Fund Size: {ExtendedLiquid.currencyMillions(fundSize, true)}
          </StyledTypography>
        ) : null}
      </Stack>

      {showPipelineOverlay && (
        <Box
          className="action-overlay"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: 0,
            visibility: 'hidden',
            transition: 'opacity 0.2s ease',
            zIndex: 10,
          }}
        >
          <StyledTypography
            sx={{
              color: 'white',
              fontSize: '0.6rem',
              fontWeight: 700,
              marginBottom: '6px',
              textAlign: 'center',
              lineHeight: 1.1,
            }}
          >
            {fundName || 'Unnamed Opportunity'}
          </StyledTypography>
          <Stack direction="row" spacing={0.5}>
            {investor && (
              <Tooltip title="View Investor">
                <IconButton
                  aria-label="View Investor"
                  onClick={handleViewInvestor}
                  sx={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                  }}
                >
                  <VisibilityIcon sx={{ fontSize: '12px' }} />
                </IconButton>
              </Tooltip>
            )}
            {!previewMode && (
              <>
                <Tooltip title="View Fund">
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(`/opportunities/${fundOpportunityId}`);
                    }}
                    sx={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      color: 'white',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    <ApartmentIcon sx={{ fontSize: '12px' }} />
                  </IconButton>
                </Tooltip>
                {onPromote && (
                  <Tooltip title="Promote">
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        onPromote();
                      }}
                      sx={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                      }}
                    >
                      <ArrowForwardIcon sx={{ fontSize: '12px' }} />
                    </IconButton>
                  </Tooltip>
                )}
                {onDemote && (
                  <Tooltip title="Demote">
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        onDemote();
                      }}
                      sx={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                      }}
                    >
                      <ArrowBackIcon sx={{ fontSize: '12px' }} />
                    </IconButton>
                  </Tooltip>
                )}
                {onDelete && (
                  <Tooltip title="Delete">
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        onDelete();
                      }}
                      sx={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: '12px' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Stack>
        </Box>
      )}
    </StyledCard>
  );
};

export default FundOpportunityPipelineCard;
